// components/fuel/TruckModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Field, ModalFooter } from './modalComponents';
import { API_BASE, FUEL_TYPES, TRUCK_STATUS_CONFIG } from './constants';
import axios from 'axios';

const TruckModal = ({ isOpen, onClose, editingTruck, truckForm, setTruckForm, onSave, saving }) => {
  const [loading, setLoading] = useState(false);
  
  // Données chargées depuis la base
  const [existingTrucks, setExistingTrucks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [truckNumberError, setTruckNumberError] = useState('');

  // Charger toutes les données depuis la base
  useEffect(() => {
    if (isOpen) {
      const loadAllData = async () => {
        setLoading(true);
        try {
          // 1. Charger tous les trucks existants
          const trucksRes = await axios.get(`${API_BASE}/fuel/trucks?active_only=false`);
          setExistingTrucks(trucksRes.data);
          
          // 2. Charger les staff (conducteurs)
          const staffRes = await axios.get(`${API_BASE}/staff/`);
          setStaffList(staffRes.data);
          
        } catch (err) {
          console.error('Failed to load data', err);
        } finally {
          setLoading(false);
        }
      };
      loadAllData();
    }
  }, [isOpen]);

  const setForm = (key, val) => {
    setTruckForm(prev => ({ ...prev, [key]: val }));
    
    // Vérifier si le numéro de truck existe déjà
    if (key === 'truck_number' && val && !editingTruck) {
      const exists = existingTrucks.some(t => t.truck_number === val);
      setTruckNumberError(exists ? 'This truck number already exists' : '');
    }
    
    // Si on sélectionne un staff, remplir automatiquement nom et téléphone
    if (key === 'driver_staff_id') {
      const selectedStaff = staffList.find(s => s.id === val);
      if (selectedStaff) {
        setTruckForm(prev => ({
          ...prev,
          driver_staff_id: val,
          driver_name: `${selectedStaff.first_name} ${selectedStaff.last_name}`,
          driver_phone: selectedStaff.phone || selectedStaff.mobile || ''
        }));
      }
    }
  };

  // Récupérer les valeurs uniques pour chaque champ
  const uniqueTruckNumbers = [...new Set(existingTrucks.map(t => t.truck_number).filter(v => v))];
  const uniqueTruckNames = [...new Set(existingTrucks.map(t => t.truck_name).filter(v => v))];
  const uniqueLicensePlates = [...new Set(existingTrucks.map(t => t.license_plate).filter(v => v))];

  if (!isOpen) return null;

  return (
    <Modal
      title={editingTruck 
        ? `Edit Truck · ${editingTruck.truck_number}`
        : 'Add Fuel Truck'}
      onClose={onClose}
    >
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        
        {loading && (
          <div className="text-center py-4 text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
            Loading data from database...
          </div>
        )}

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
          🚛 All fields are loaded dynamically from PostgreSQL database
        </div>

        {/* ===== SECTION 1: IDENTIFICATION ===== */}
        <div className="bg-gray-700/20 rounded-lg p-3">
          <p className="text-gray-400 text-xs mb-2 font-medium">📋 Identification</p>
          <div className="grid grid-cols-2 gap-3">
            
            {/* Truck Number - SELECT depuis base */}
            <Field label="Truck Number *">
              <select 
                value={truckForm.truck_number || ''} 
                onChange={e => setForm('truck_number', e.target.value)}
                className={`w-full bg-gray-700 border rounded-lg p-2 text-white text-sm ${
                  truckNumberError ? 'border-red-500' : 'border-gray-600'
                }`}
              >
                <option value="">— Select truck number —</option>
                {uniqueTruckNumbers.map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
              <input 
                type="text" 
                placeholder="Or enter new truck number"
                value={truckForm.truck_number || ''} 
                onChange={e => setForm('truck_number', e.target.value.toUpperCase())}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm mt-2"
              />
              {truckNumberError && (
                <p className="text-red-400 text-xs mt-1">{truckNumberError}</p>
              )}
            </Field>
            
            {/* Truck Name - SELECT depuis base */}
            <Field label="Truck Name">
              <select 
                value={truckForm.truck_name || ''} 
                onChange={e => setForm('truck_name', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="">— Select truck name —</option>
                {uniqueTruckNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <input 
                type="text" 
                placeholder="Or enter new truck name"
                value={truckForm.truck_name || ''} 
                onChange={e => setForm('truck_name', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm mt-2"
              />
            </Field>
          </div>

          {/* License Plate - SELECT depuis base */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="License Plate">
              <select 
                value={truckForm.license_plate || ''} 
                onChange={e => setForm('license_plate', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="">— Select license plate —</option>
                {uniqueLicensePlates.map(plate => (
                  <option key={plate} value={plate}>{plate}</option>
                ))}
              </select>
              <input 
                type="text" 
                placeholder="Or enter new license plate"
                value={truckForm.license_plate || ''} 
                onChange={e => setForm('license_plate', e.target.value.toUpperCase())}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm mt-2"
              />
            </Field>
            
            {/* Status - SELECT depuis ENUM */}
            <Field label="Status">
              <select 
                value={truckForm.status || 'available'} 
                onChange={e => setForm('status', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                {Object.entries(TRUCK_STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.icon} {config.label}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        {/* ===== SECTION 2: FUEL CONFIGURATION ===== */}
        <div className="bg-gray-700/20 rounded-lg p-3">
          <p className="text-gray-400 text-xs mb-2 font-medium">⛽ Fuel Configuration</p>
          <div className="grid grid-cols-2 gap-3">
            
            {/* Fuel Type - SELECT depuis fuel_inventory */}
            <Field label="Fuel Type *">
              <select 
                value={truckForm.fuel_type || ''} 
                onChange={e => setForm('fuel_type', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="">— Select fuel type —</option>
                {FUEL_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </Field>
            
            {/* Capacity - Input libre */}
            <Field label="Capacity (gallons) *">
              <input 
                type="number" 
                min="0" 
                step="100"
                placeholder="e.g., 3000"
                value={truckForm.capacity_gallons || ''} 
                onChange={e => setForm('capacity_gallons', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
              />
            </Field>
            
            {/* Current Volume - Input libre */}
            <Field label="Current Volume (gal)">
              <input 
                type="number" 
                min="0" 
                step="10"
                placeholder="Initial fuel level"
                value={truckForm.current_volume_gallons || 0} 
                onChange={e => setForm('current_volume_gallons', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
              />
            </Field>
          </div>
        </div>

        {/* ===== SECTION 3: DRIVER INFORMATION ===== */}
        <div className="bg-gray-700/20 rounded-lg p-3">
          <p className="text-gray-400 text-xs mb-2 font-medium">👤 Driver Information (from Staff table)</p>
          
          {/* Staff Selection - SELECT depuis base */}
          <Field label="Select Staff Driver">
            <select 
              value={truckForm.driver_staff_id || ''} 
              onChange={e => setForm('driver_staff_id', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              disabled={loading}
            >
              <option value="">— Select a staff member —</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.first_name} {staff.last_name} - {staff.role || 'Staff'}
                </option>
              ))}
            </select>
          </Field>
          
          {/* Driver Name - Auto-filled depuis staff */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Driver Name">
              <input 
                type="text" 
                placeholder="Auto-filled from staff"
                value={truckForm.driver_name || ''} 
                readOnly
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2 text-gray-400 text-sm cursor-not-allowed" 
              />
            </Field>
            
            {/* Driver Phone - Auto-filled depuis staff */}
            <Field label="Driver Phone">
              <input 
                type="text" 
                placeholder="Auto-filled from staff"
                value={truckForm.driver_phone || ''} 
                readOnly
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2 text-gray-400 text-sm cursor-not-allowed" 
              />
            </Field>
          </div>
        </div>

        {/* ===== SECTION 4: MAINTENANCE SCHEDULE ===== */}
        <div className="bg-gray-700/20 rounded-lg p-3">
          <p className="text-gray-400 text-xs mb-2 font-medium">🔧 Maintenance Schedule</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Last Maintenance">
              <input 
                type="date"
                value={truckForm.last_maintenance_date || ''} 
                onChange={e => setForm('last_maintenance_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
              />
            </Field>
            <Field label="Next Maintenance">
              <input 
                type="date"
                value={truckForm.next_maintenance_date || ''} 
                onChange={e => setForm('next_maintenance_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
              />
            </Field>
          </div>
        </div>

        {/* ===== SECTION 5: NOTES ===== */}
        <Field label="Notes">
          <textarea 
            placeholder="Additional notes about this truck..."
            value={truckForm.notes || ''} 
            onChange={e => setForm('notes', e.target.value)}
            rows="2"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
          />
        </Field>

        {/* ===== SECTION 6: ACTIVE STATUS ===== */}
        <Field label="Active">
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox"
              checked={truckForm.is_active !== false}
              onChange={e => setForm('is_active', e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500"
            />
            <span className="text-gray-300 text-sm">Vehicle is in active service</span>
          </label>
        </Field>
      </div>
      
      <ModalFooter 
        onCancel={onClose} 
        onSave={onSave}
        saving={saving} 
        disabled={!truckForm.truck_number || !truckForm.fuel_type || !truckForm.capacity_gallons || !!truckNumberError}
        label={editingTruck ? 'Save Changes' : 'Create Truck'} 
      />
    </Modal>
  );
};

export default TruckModal;